import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { DashboardCounts, getDashboardCounts } from '../services/dashboard';
import { useVisiblePolling } from '../hooks/useVisiblePolling';
import { useLayoutState } from './LayoutContext';
import { isAuthenticated } from '../services/auth';

/**
 * DashboardCountsContext - Single source of truth for sidebar/navbar badge counts.
 *
 * Replaces five independent pollers (one each for active tasks, task counts,
 * email counts, pending transactions, unread notifications) with a single
 * /api/v1/dashboard/counts poll. This drops sustained traffic on the Gunicorn
 * worker from 5 req/min/user to 1 req/min/user.
 *
 * Behavior:
 * - Polls once per 60s while the tab is visible (paused when hidden).
 * - Re-polls immediately when selectedAccountId changes.
 * - Listens for the existing 'refreshTaskCount' / 'refreshEmailCount' window
 *   events so mutation flows (TaskModal, email actions, harmonize commit)
 *   keep their existing refresh contract.
 * - Exposes refresh() so future call sites can request an immediate update.
 *
 * Consumers should prefer the merged counts here over calling the per-resource
 * count endpoints directly.
 */

interface DashboardCountsContextValue {
  counts: DashboardCounts | null;
  isLoading: boolean;
  refresh: () => void;
}

const DEFAULT_COUNTS: DashboardCounts = {
  emails: { total_inbox: 0, total_unread: 0 },
  tasks: { active: 0, total_unfinished: 0, overdue: 0 },
  transactions: { pending: 0 },
  notifications: { unread: 0 },
};

const DashboardCountsContext = createContext<DashboardCountsContextValue | undefined>(undefined);

interface DashboardCountsProviderProps {
  children: ReactNode;
}

export const DashboardCountsProvider: React.FC<DashboardCountsProviderProps> = ({ children }) => {
  const { selectedAccountId } = useLayoutState();
  const [counts, setCounts] = useState<DashboardCounts | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCounts = useCallback(async () => {
    // Skip when unauthenticated. The provider is mounted at the app root so it
    // renders on public pages (e.g. /login) too; firing a 401 from here poisons
    // auth state via the refresh interceptor.
    if (!isAuthenticated()) {
      return;
    }
    setIsLoading(true);
    try {
      const result = await getDashboardCounts({
        emailAccountId: selectedAccountId || undefined,
      });
      setCounts(result);
    } catch (err) {
      // Mirror per-endpoint poller behavior: log and keep previous values so
      // a transient failure doesn't reset badges to 0.
      console.error('Failed to load dashboard counts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedAccountId]);

  useVisiblePolling(fetchCounts, 60000, [selectedAccountId]);

  useEffect(() => {
    const handler = () => {
      if (!isAuthenticated()) return;
      fetchCounts();
    };
    window.addEventListener('refreshTaskCount', handler);
    window.addEventListener('refreshEmailCount', handler);
    return () => {
      window.removeEventListener('refreshTaskCount', handler);
      window.removeEventListener('refreshEmailCount', handler);
    };
  }, [fetchCounts]);

  const value = useMemo<DashboardCountsContextValue>(() => ({
    counts,
    isLoading,
    refresh: fetchCounts,
  }), [counts, isLoading, fetchCounts]);

  return (
    <DashboardCountsContext.Provider value={value}>
      {children}
    </DashboardCountsContext.Provider>
  );
};

/**
 * Read the latest merged dashboard counts. Returns DEFAULT_COUNTS until the
 * first successful fetch resolves so callers can render badges without
 * null-checks.
 */
export const useDashboardCounts = (): {
  counts: DashboardCounts;
  isLoading: boolean;
  refresh: () => void;
} => {
  const ctx = useContext(DashboardCountsContext);
  if (!ctx) {
    throw new Error('useDashboardCounts must be used within DashboardCountsProvider');
  }
  return {
    counts: ctx.counts ?? DEFAULT_COUNTS,
    isLoading: ctx.isLoading,
    refresh: ctx.refresh,
  };
};
